import React, { useCallback, useEffect, useState } from "react";
import api, { inr, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip, PieChart, Pie, Cell, Legend } from "recharts";
import { CurrencyInr, ArrowsCounterClockwise, Wallet, DownloadSimple, CreditCard, PencilSimple, ArrowUUpLeft } from "@phosphor-icons/react";
import { toast } from "sonner";
import CustomerLink from "@/components/CustomerLink";

const METHOD_COLORS = { Cash: "#EA580C", UPI: "#16A34A", Card: "#1E3A8A", "Net Banking": "#7C3AED", Wallet: "#F59E0B" };
const PAY_METHODS = ["Cash", "UPI", "PhonePe", "Google Pay", "Paytm", "Card", "Net Banking", "NEFT", "RTGS", "IMPS", "Wallet", "Cheque"];

function Stat({ label, value, tone, icon: Icon, testid }) {
  const t = { orange: "bg-orange-50 text-orange-600 border-orange-200", green: "bg-emerald-50 text-emerald-700 border-emerald-200", blue: "bg-blue-50 text-blue-700 border-blue-200", amber: "bg-amber-50 text-amber-700 border-amber-200" }[tone];
  return (
    <Card className="p-5 bg-white border-slate-200" data-testid={testid}>
      <div className={`w-10 h-10 rounded-md flex items-center justify-center border ${t} mb-3`}><Icon size={20} weight="duotone" /></div>
      <div className="text-3xl font-bold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>{value}</div>
      <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mt-1">{label}</div>
    </Card>
  );
}

export default function Payments() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [edit, setEdit] = useState(null);       // {id, app_no, method, txn_ref, total, customer_name}
  const [reverse, setReverse] = useState(null); // same shape

  const load = useCallback(() => {
    api.get("/payments/reconciliation", { params: { days } }).then((r) => setData(r.data));
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const saveEdit = async () => {
    if (!edit) return;
    try {
      await api.patch(`/applications/${edit.id}/payment`, { method: edit.method, txn_ref: edit.txn_ref || "" });
      toast.success(`${edit.app_no} · payment updated`);
      setEdit(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const confirmReverse = async () => {
    if (!reverse) return;
    try {
      await api.post(`/applications/${reverse.id}/unpay`);
      toast.success(`${reverse.app_no} · payment reversed to Unpaid`);
      setReverse(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const exportCsv = () => {
    if (!data) return;
    const header = ["Date/Time", "App #", "Customer", "Service", "Method", "Txn Ref", "Amount", "Operator"];
    const rows = data.recent_paid.map((p) => [p.paid_at, p.app_no, p.customer_name, p.service_name, p.method, p.txn_ref || "", p.total, p.paid_by || ""]);
    const csv = [header, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `payments-${new Date().toISOString().slice(0,10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-8 max-w-[1400px]">
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-orange-600 font-semibold mb-2">Ledger</div>
          <h1 className="text-4xl font-bold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>Payments & Reconciliation</h1>
          <p className="text-sm text-slate-600 mt-2">Daily collection, method-wise breakdown, and operator totals in one place.</p>
        </div>
        <div className="flex gap-2 items-center">
          <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
            <SelectTrigger className="w-40 h-11" data-testid="period-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Today</SelectItem>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="365">Last year</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={exportCsv} data-testid="export-csv-btn" className="bg-slate-900 hover:bg-slate-800 text-white h-11 gap-2"><DownloadSimple size={16} /> Export CSV</Button>
        </div>
      </div>

      {!data ? (
        <div className="text-slate-500 py-20 text-center">Loading…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
            <Stat label="Today's Collection" value={inr(data.today_total)} tone="orange" icon={CurrencyInr} testid="stat-today" />
            <Stat label={`${data.period_days}-day Total`} value={inr(data.total_collected)} tone="green" icon={Wallet} testid="stat-total" />
            <Stat label="GST Collected" value={inr(data.gst_collected)} tone="blue" icon={CreditCard} testid="stat-gst" />
            <Stat label={`Outstanding (${data.outstanding_count})`} value={inr(data.outstanding)} tone="amber" icon={ArrowsCounterClockwise} testid="stat-outstanding" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            <Card className="lg:col-span-2 p-6 bg-white border-slate-200" data-testid="chart-daily">
              <div className="mb-4">
                <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Trend</div>
                <h3 className="text-xl font-semibold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>Daily Collection</h3>
              </div>
              <div style={{ width: "100%", height: 260 }}>
                <ResponsiveContainer>
                  <BarChart data={data.by_day}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                    <XAxis dataKey="date" stroke="#64748B" fontSize={11} />
                    <YAxis stroke="#64748B" fontSize={11} />
                    <Tooltip contentStyle={{ background: "#0F172A", border: "none", borderRadius: 8, color: "#fff" }} formatter={(v) => inr(v)} />
                    <Bar dataKey="amount" fill="#EA580C" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>

            <Card className="p-6 bg-white border-slate-200" data-testid="chart-method">
              <div className="mb-4">
                <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Breakdown</div>
                <h3 className="text-xl font-semibold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>By Payment Method</h3>
              </div>
              {data.by_method.length === 0 ? <div className="text-sm text-slate-500 py-10 text-center">No payments yet.</div> : (
                <div style={{ width: "100%", height: 240 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie data={data.by_method} dataKey="amount" nameKey="method" cx="50%" cy="50%" innerRadius={45} outerRadius={80} paddingAngle={2}>
                        {data.by_method.map((m) => <Cell key={m.method} fill={METHOD_COLORS[m.method] || "#94A3B8"} />)}
                      </Pie>
                      <Tooltip formatter={(v) => inr(v)} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Card>
          </div>

          {data.by_operator.length > 0 && (
            <Card className="p-6 bg-white border-slate-200 mb-8" data-testid="table-operator">
              <div className="mb-3">
                <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Team</div>
                <h3 className="text-xl font-semibold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>Operator-wise Collection</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                {data.by_operator.map((o) => (
                  <div key={o.operator} className="p-3 rounded-md border border-slate-200 bg-slate-50">
                    <div className="text-sm font-medium text-slate-900 truncate">{o.operator}</div>
                    <div className="text-lg font-bold text-orange-600" style={{ fontFamily: "var(--font-heading)" }}>{inr(o.amount)}</div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          <Card className="bg-white border-slate-200 overflow-hidden" data-testid="recent-payments-table">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Recent</div>
                <h3 className="text-xl font-semibold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>Latest Payments</h3>
              </div>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs uppercase tracking-[0.15em] text-slate-500">Date/Time</TableHead>
                  <TableHead className="text-xs uppercase tracking-[0.15em] text-slate-500">App #</TableHead>
                  <TableHead className="text-xs uppercase tracking-[0.15em] text-slate-500">Customer</TableHead>
                  <TableHead className="text-xs uppercase tracking-[0.15em] text-slate-500">Service</TableHead>
                  <TableHead className="text-xs uppercase tracking-[0.15em] text-slate-500">Method</TableHead>
                  <TableHead className="text-xs uppercase tracking-[0.15em] text-slate-500">Txn Ref</TableHead>
                  <TableHead className="text-xs uppercase tracking-[0.15em] text-slate-500 text-right">Amount</TableHead>
                  <TableHead className="text-xs uppercase tracking-[0.15em] text-slate-500 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.recent_paid.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-slate-500 py-16">No payments in this period.</TableCell></TableRow>}
                {data.recent_paid.map((p) => (
                  <TableRow key={`${p.app_no}-${p.paid_at}`} className="hover:bg-slate-50">
                    <TableCell className="text-xs font-mono text-slate-600">{p.paid_at?.slice(0, 16).replace("T", " ")}</TableCell>
                    <TableCell className="font-mono text-xs">{p.app_no}</TableCell>
                    <TableCell><CustomerLink id={p.customer_id} name={p.customer_name} /></TableCell>
                    <TableCell className="text-sm">{p.service_name}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs font-medium" style={{ borderColor: (METHOD_COLORS[p.method] || "#94A3B8") + "80", color: METHOD_COLORS[p.method] || "#334155" }}>
                        {p.method}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs font-mono text-slate-500">{p.txn_ref || "—"}</TableCell>
                    <TableCell className="text-right font-semibold">{inr(p.total)}</TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex gap-1">
                        <Button
                          size="sm" variant="outline"
                          disabled={!p.id}
                          data-testid={`pay-edit-${p.app_no}`}
                          onClick={() => setEdit({ id: p.id, app_no: p.app_no, method: p.method, txn_ref: p.txn_ref || "", total: p.total, customer_name: p.customer_name })}
                          className="h-8 px-2 gap-1 text-xs border-slate-300 text-slate-700 hover:bg-slate-100"
                          title="Edit method / Txn ref"
                        >
                          <PencilSimple size={12} /> Edit
                        </Button>
                        <Button
                          size="sm" variant="outline"
                          disabled={!p.id}
                          data-testid={`pay-reverse-${p.app_no}`}
                          onClick={() => setReverse({ id: p.id, app_no: p.app_no, method: p.method, total: p.total, customer_name: p.customer_name })}
                          className="h-8 px-2 gap-1 text-xs border-red-300 text-red-700 hover:bg-red-50"
                          title="Reverse this payment"
                        >
                          <ArrowUUpLeft size={12} /> Reverse
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </>
      )}

      {/* Edit payment dialog */}
      <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader><DialogTitle style={{ fontFamily: "var(--font-heading)" }}>Edit Payment</DialogTitle></DialogHeader>
          {edit && (
            <div className="py-2 space-y-4">
              <div className="text-sm text-slate-600">
                <span className="font-mono">{edit.app_no}</span> · {edit.customer_name} · <span className="font-semibold text-slate-900">{inr(edit.total)}</span>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-600">Method</Label>
                <div className="grid grid-cols-5 gap-1.5">
                  {PAY_METHODS.map((m) => (
                    <button
                      key={m}
                      type="button"
                      data-testid={`pay-edit-method-${m.replace(/\s/g, "-").toLowerCase()}`}
                      onClick={() => setEdit({ ...edit, method: m })}
                      className={`px-2 py-2 rounded-md border text-xs font-medium transition-colors ${
                        edit.method === m
                          ? "bg-slate-900 text-white border-slate-900"
                          : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"
                      }`}
                    >
                      {m}
                    </button>
                  ))}
                </div>
              </div>
              {edit.method !== "Cash" && (
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-600">Txn Ref / UTR</Label>
                  <Input
                    data-testid="pay-edit-txn-ref"
                    value={edit.txn_ref || ""}
                    onChange={(e) => setEdit({ ...edit, txn_ref: e.target.value })}
                    placeholder={edit.method === "UPI" ? "e.g. 4267...@ybl" : "Reference / RRN"}
                  />
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEdit(null)}>Cancel</Button>
            <Button data-testid="pay-edit-save" onClick={saveEdit} className="bg-orange-600 hover:bg-orange-700 text-white">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reverse payment confirmation */}
      <AlertDialog open={!!reverse} onOpenChange={(v) => !v && setReverse(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle style={{ fontFamily: "var(--font-heading)" }}>Reverse this payment?</AlertDialogTitle>
            <AlertDialogDescription>
              {reverse && (
                <>
                  <span className="font-mono">{reverse.app_no}</span> · {reverse.customer_name}
                  {" — "}
                  <span className="font-semibold">{inr(reverse.total)}</span> ({reverse.method})
                  <span className="block mt-3 text-sm text-slate-700">This will move the application back to <b>Unpaid</b> and clear the recorded transaction. A reversal entry will be written to the audit log.</span>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="pay-reverse-confirm" onClick={confirmReverse} className="bg-red-600 hover:bg-red-700 text-white">Yes, reverse it</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
